import { ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect, type ReactNode } from 'react';

interface Props {
  title: string;
  icon: ReactNode;
  accentColor?: string;
  defaultOpen?: boolean;
  forceOpen?: boolean;
  badge?: number;
  children: ReactNode;
}

export default function PanelWrapper({ title, icon, accentColor = 'primary', defaultOpen = false, forceOpen = false, badge, children }: Props) {
  const [open, setOpen] = useState(defaultOpen);

  // Force open when forceOpen prop changes to true
  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  return (
    <div className={`rounded-lg overflow-hidden transition-all duration-200 panel-neon-left ${open ? 'active glow-teal-subtle' : ''}`}
      style={{ border: `1px solid hsl(var(--border) / ${open ? '0.6' : '0.3'})` }}
    >
      <button
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-all duration-200 ${
          open ? 'panel-header-gradient' : 'hover:bg-muted/30'
        }`}
      >
        <div className={`w-6 h-6 rounded-md flex items-center justify-center transition-all duration-200 ${
          open
            ? 'bg-primary/15 text-primary'
            : 'bg-muted/50 text-muted-foreground'
        }`}>
          {icon}
        </div>
        <span className={`text-[11px] font-semibold uppercase tracking-wider flex-1 transition-colors duration-200 truncate ${
          open ? 'text-foreground' : 'text-muted-foreground'
        }`}>
          {title}
        </span>
        {badge !== undefined && badge > 0 && (
          <span className="element-badge bg-primary/20 text-primary border border-primary/30">
            {badge}
          </span>
        )}
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.15 }}>
          <ChevronDown className={`w-3.5 h-3.5 transition-colors ${open ? 'text-primary' : 'text-muted-foreground/50'}`} />
        </motion.div>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 pt-2 space-y-3 border-t border-border/30">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
